import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_ORDER = ['personnel', 'administrative', 'project', 'women_project'];

const staffName = (s) => s.full_name || s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim();

/**
 * Renders: Category Group → Subcategory → Women Activity → dynamic subcategory fields.
 * value = { category_group, subcategory, women_activity, details }
 */
export const TaxonomyFieldsEditor = ({ taxonomy, value, onChange, idPrefix = 'tax' }) => {
  const [staff, setStaff] = useState([]);
  const fields = taxonomy?.groups?.[value.category_group]?.subcategories
    ?.find((s) => s.key === value.subcategory)?.fields || [];
  const needsStaff = fields.some((f) => f.type === 'staff_select');

  useEffect(() => {
    if (!needsStaff || staff.length) return;
    axios.get(`${API_URL}/api/personnel/staff`)
      .then((r) => setStaff(Array.isArray(r.data) ? r.data : r.data.items || []))
      .catch(() => {});
  }, [needsStaff, staff.length]);

  if (!taxonomy) return null;
  const setDetail = (key, val) => onChange({ ...value, details: { ...(value.details || {}), [key]: val } });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Expense Category *</Label>
          <Select
            value={value.category_group}
            onValueChange={(v) => onChange({ ...value, category_group: v, subcategory: '', women_activity: '', details: {} })}
          >
            <SelectTrigger data-testid={`${idPrefix}-group-select`}><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {GROUP_ORDER.filter((g) => taxonomy.groups[g]).map((g) => (
                <SelectItem key={g} value={g}>{taxonomy.groups[g].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {value.category_group && (
          <div className="space-y-1.5">
            <Label>Subcategory *</Label>
            <Select
              value={value.subcategory}
              onValueChange={(v) => onChange({ ...value, subcategory: v, details: {} })}
            >
              <SelectTrigger data-testid={`${idPrefix}-subcategory-select`}><SelectValue placeholder="Select subcategory" /></SelectTrigger>
              <SelectContent>
                {taxonomy.groups[value.category_group].subcategories.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {value.category_group === 'women_project' && (
        <div className="space-y-1.5">
          <Label>Women Project Activity / Enterprise *</Label>
          <Select value={value.women_activity} onValueChange={(v) => onChange({ ...value, women_activity: v })}>
            <SelectTrigger data-testid={`${idPrefix}-women-activity-select`}><SelectValue placeholder="Select activity" /></SelectTrigger>
            <SelectContent>
              {(taxonomy.women_activities || []).map((a) => (
                <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {fields.length > 0 && (
        <div className="border rounded-lg p-3 space-y-3 bg-muted/30" data-testid={`${idPrefix}-dynamic-fields`}>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {taxonomy.groups[value.category_group]?.subcategories.find((s) => s.key === value.subcategory)?.label} details
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((f) => {
              const val = (value.details || {})[f.key] ?? '';
              const label = <Label className="text-xs">{f.label}{f.required && ' *'}</Label>;
              if (f.type === 'textarea') {
                return (
                  <div key={f.key} className="space-y-1 sm:col-span-2">
                    {label}
                    <Textarea rows={2} value={val} onChange={(e) => setDetail(f.key, e.target.value)}
                      data-testid={`${idPrefix}-field-${f.key}`} />
                  </div>
                );
              }
              if (f.type === 'select') {
                return (
                  <div key={f.key} className="space-y-1">
                    {label}
                    <Select value={val} onValueChange={(v) => setDetail(f.key, v)}>
                      <SelectTrigger data-testid={`${idPrefix}-field-${f.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (
                          <SelectItem key={o} value={o} className="capitalize">{o.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              if (f.type === 'staff_select') {
                return (
                  <div key={f.key} className="space-y-1">
                    {label}
                    {staff.length > 0 ? (
                      <Select value={val} onValueChange={(v) => setDetail(f.key, v)}>
                        <SelectTrigger data-testid={`${idPrefix}-field-${f.key}`}><SelectValue placeholder="Select staff" /></SelectTrigger>
                        <SelectContent>
                          {staff.map((s) => (
                            <SelectItem key={s.id} value={staffName(s) || s.id}>{staffName(s) || s.id}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={val} placeholder="Staff member name"
                        onChange={(e) => setDetail(f.key, e.target.value)} data-testid={`${idPrefix}-field-${f.key}`} />
                    )}
                  </div>
                );
              }
              if (f.type === 'checkbox') {
                return (
                  <div key={f.key} className="flex items-center gap-2 pt-5">
                    <Checkbox checked={!!val} onCheckedChange={(c) => setDetail(f.key, !!c)}
                      data-testid={`${idPrefix}-field-${f.key}`} id={`${idPrefix}-${f.key}`} />
                    <Label htmlFor={`${idPrefix}-${f.key}`} className="text-xs cursor-pointer">{f.label}</Label>
                  </div>
                );
              }
              return (
                <div key={f.key} className="space-y-1">
                  {label}
                  <Input
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                    value={val}
                    onChange={(e) => setDetail(f.key, f.type === 'number' ? e.target.value : e.target.value)}
                    data-testid={`${idPrefix}-field-${f.key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
